import { sx } from '../../../lib/sx'
import { Hov } from '../../../lib/Hov'
import type { Vals } from '../../../demo/vals'

/**
 * Three ways to add someone, in descending order of how well they work when
 * the other person is standing next to you: scan, share a link, send an email.
 */
export function Invite({ v }: { v: Vals }) {
  return (
    <div style={sx('position:absolute;inset:0;padding-top:64px;display:flex;flex-direction:column')}>
      <div style={sx('flex:1;min-height:0;overflow:auto;padding:0 20px 24px;display:flex;flex-direction:column;gap:14px')}>
        <button
          onClick={v.tabGroup}
          style={sx(
            'width:36px;height:36px;flex:none;border-radius:var(--radius-full);border:none;' +
              'background:var(--bg-tertiary);cursor:pointer;display:flex;align-items:center;justify-content:center'
          )}
        >
          <span
            style={sx(
              'width:18px;height:18px;background:var(--text-primary);' +
                '-webkit-mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-left.svg) center/contain no-repeat'
            )}
          />
        </button>

        <div style={sx('display:flex;flex-direction:column;gap:8px;margin-bottom:2px')}>
          <span
            style={sx(
              'font-size:var(--fs-5xl);font-weight:var(--fw-bold);letter-spacing:var(--ls-sm);line-height:34px'
            )}
          >
            Add members to your group plan
          </span>
          <span style={sx('font-size:var(--fs-xs);line-height:18px;color:var(--text-secondary)')}>
            Members get their own Nord account and control their own privacy. Group features are
            opt-in only and never share private information.
          </span>
        </div>

        {/* Scanning first: it is the only option that needs nothing typed. */}
        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;' +
              'align-items:center;gap:14px'
          )}
        >
          <span
            style={sx(
              'width:172px;height:172px;border-radius:var(--radius-md);background:#fff;padding:10px;display:flex'
            )}
          >
            {v.qrInviteImg}
          </span>
          <span style={sx('display:flex;flex-direction:column;gap:4px;align-items:center;text-align:center')}>
            <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
              Scan the code and follow instructions
            </span>
            <span
              style={sx(
                'font-size:var(--fs-2xs);font-family:ui-monospace,Menlo,monospace;' +
                  'color:var(--text-secondary);letter-spacing:.02em'
              )}
            >
              nordvpn.com/join/8FQ2-14KD
            </span>
          </span>
        </div>

        <Hov
          as="button"
          onClick={v.openShareSheet}
          style={sx(
            'width:100%;text-align:left;padding:16px 18px;border-radius:var(--radius-lg);' +
              'background:var(--bg-secondary);border:1px solid var(--border-secondary);' +
              'color:var(--text-primary);font-family:inherit;cursor:pointer;' +
              'display:flex;align-items:center;gap:12px'
          )}
          hoverStyle={sx('background:var(--bg-tertiary)')}
        >
          <span style={sx('flex:1;display:flex;flex-direction:column;gap:4px;min-width:0')}>
            <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
              Add a member
            </span>
            <span style={sx('font-size:var(--fs-xs);line-height:17px;color:var(--text-secondary)')}>
              Share link so they can sign up or log in with their account to accept your invite.
            </span>
          </span>
          <span
            style={sx(
              'width:16px;height:16px;flex:none;opacity:.5;background:var(--text-primary);' +
                '-webkit-mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat;' +
                'mask:url(/assets/icons/arrow-right.svg) center/contain no-repeat'
            )}
          />
        </Hov>

        <div
          style={sx(
            'padding:18px;border-radius:var(--radius-lg);background:var(--bg-secondary);' +
              'border:1px solid var(--border-secondary);display:flex;flex-direction:column;gap:12px'
          )}
        >
          <span style={sx('font-size:var(--fs-sm);font-weight:var(--fw-medium)')}>
            Invite by email
          </span>

          <button
            onClick={v.pickInvitee}
            style={sx(
              `height:52px;border-radius:var(--radius-md);border:1px solid ${v.inviteFieldBorder};` +
                `background:var(--bg-primary);cursor:pointer;display:flex;align-items:center;` +
                `gap:10px;padding:0 14px;font-family:inherit;color:${v.inviteFieldColor};` +
                `font-size:var(--fs-sm);text-align:left`
            )}
          >
            <span
              style={sx(
                'width:18px;height:18px;flex:none;background:var(--text-secondary);' +
                  '-webkit-mask:url(/assets/icons/email.svg) center/contain no-repeat;' +
                  'mask:url(/assets/icons/email.svg) center/contain no-repeat'
              )}
            />
            <span style={sx('flex:1;min-width:0')}>{v.inviteEmail}</span>
            {v.invitePicked ? (
              <span
                style={sx(
                  'width:18px;height:18px;flex:none;background:var(--text-success);' +
                    '-webkit-mask:url(/assets/icons/checkmark.svg) center/contain no-repeat;' +
                    'mask:url(/assets/icons/checkmark.svg) center/contain no-repeat'
                )}
              />
            ) : null}
          </button>

          <Hov
            as="button"
            onClick={v.sendInvite}
            style={sx(
              'height:48px;border-radius:var(--radius-full);border:none;background:var(--bg-accent);' +
                'color:#fff;font-family:inherit;font-size:var(--fs-sm);font-weight:var(--fw-medium);' +
                'cursor:pointer;transition:all 150ms var(--ease-out)'
            )}
            hoverStyle={sx('background:var(--bg-accent-active)')}
          >
            Send invite
          </Hov>
        </div>
      </div>
    </div>
  )
}
